// Model routing table (PRD "Model routing and cost guardrails").
// Stored as configuration in D1 (settings.routing) so ops can change it without a deploy (MR-4).
// This file holds the defaults and the price list used for cost accounting.

export type Provider = 'openrouter' | 'anthropic'

export interface ModelRef {
  provider: Provider
  model: string
  /** Send through the provider's batch API (half price, higher latency). */
  batch?: boolean
}

export type PipelineStep =
  | 'page_type_naming'
  | 'preview_build'
  | 'build'
  | 'fix'
  | 'review'
  | 'preview_review'
  | 'change_request'
  | 'note_triage'

export interface StepRoute {
  primary: ModelRef
  fallback: ModelRef
  /** Paid orders wait for DeepSeek off-peak prices (MR-2). */
  offPeak?: boolean
}

export interface RoutingConfig {
  steps: Record<PipelineStep, StepRoute>
  /** Run budget = multiplier x expected cost for the profile (MR-1). */
}

const FLASH: ModelRef = { provider: 'openrouter', model: 'deepseek/deepseek-v4.1-flash' }
const SONNET: ModelRef = { provider: 'anthropic', model: 'claude-sonnet-5-5' }
const OPUS: ModelRef = { provider: 'anthropic', model: 'claude-opus-5-5' }
const HAIKU: ModelRef = { provider: 'anthropic', model: 'claude-haiku-5-5' }

export const DEFAULT_ROUTING: RoutingConfig = {
  steps: {
    page_type_naming: { primary: FLASH, fallback: HAIKU, offPeak: true },
    preview_build: { primary: FLASH, fallback: SONNET },
    build: { primary: FLASH, fallback: SONNET, offPeak: true },
    fix: { primary: FLASH, fallback: SONNET, offPeak: true },
    review: { primary: { ...OPUS, batch: true }, fallback: SONNET },
    // Previews must be ready in 30 minutes, so their review skips the batch API.
    preview_review: { primary: OPUS, fallback: SONNET },
    change_request: { primary: FLASH, fallback: SONNET },
    note_triage: { primary: HAIKU, fallback: FLASH },
  },
}

/** USD per million tokens. Batch halves both. */
/**
 * AI spend allowed per order, all jobs together. A job stops when the order passes it and the order
 * goes to Needs human; ops can raise it per order (orders.budget_usd, 0 = this default).
 */
export const ORDER_BUDGET_USD = 20

export const PRICES: Record<string, { input: number; output: number; cacheRead?: number }> = {
  'deepseek/deepseek-v4.1-flash': { input: 0.045, output: 0.5 },
  'claude-sonnet-5-5': { input: 2, output: 10, cacheRead: 0.2 },
  'claude-opus-5-5': { input: 4, output: 20, cacheRead: 0.2 },
  'claude-haiku-5-5': { input: 0.1, output: 0.5, cacheRead: 0.01 },
}

export function costUsd(
  ref: ModelRef,
  usage: { input: number; output: number; cacheRead?: number },
): number {
  const p = PRICES[ref.model]
  if (!p) return 0
  const mult = ref.batch ? 0.5 : 1
  const cache = (usage.cacheRead ?? 0) * (p.cacheRead ?? p.input)
  return ((usage.input * p.input + usage.output * p.output + cache) / 1e6) * mult
}

/**
 * DeepSeek peak windows (UTC, weekdays): 01:00-04:00 and 06:00-10:00 (MR-2).
 * Returns `at` when it is already outside a peak window, else the end of the window.
 */
export function nextOffPeak(at: Date): Date {
  const day = at.getUTCDay()
  if (day === 0 || day === 6) return at
  const h = at.getUTCHours()
  const end = (hour: number) => {
    const d = new Date(at)
    d.setUTCHours(hour, 0, 0, 0)
    return d
  }
  if (h >= 1 && h < 4) return end(4)
  if (h >= 6 && h < 10) return end(10)
  return at
}
